/**
 * ONE rule for assigning a form code, shared by the page and the server.
 *
 * Step 1 of docs/plans/2026-09-11-store-form-code.md (Jim, 2026-09-14).
 *
 * WHY ONE RULE. The page assigned codes with a letter-alternative rule for
 * clashes ("Screening Visit" and "Scheduled Visit" both derive SV; the second
 * became SCHV) while the server's readers — the data dictionary, the
 * verification inventory, the Instruction Guide — used a numeric suffix (SV,
 * SV2). The same form could therefore carry different codes in different
 * documents. The page's rule is what users see, so it is the rule that
 * survives; the numeric rule is gone from both sides.
 *
 * THE RULE. The first form carrying a natural code keeps it. A later form
 * whose natural code is taken gets, in order: the first three letters of its
 * first word plus the last letter of the base ("SCHEDULED" -> SCH + V = SCHV),
 * the first four letters of its first word (SCHE), the base plus the second
 * word's initial (SVV), and only then a numeric suffix (SV2), which always
 * terminates. Explicit codes claim their slot first, whatever their position,
 * so an override is never displaced by a derived code appearing earlier.
 * Comparison is case-insensitive; output is upper case.
 *
 * DETERMINISTIC. Same forms in, same codes out — downstream documents key on
 * these codes.
 *
 * PURE. This file is imported by the web bundle
 * (web/src/features/crf/assignFormCodes.ts re-exports it), so it must not
 * import anything that is not plain TypeScript.
 */
import { deriveFormCode } from './formCode';

export interface CodeableForm {
  name: string;
  /** An explicit code already set; never overwritten. */
  code?: string;
}

export interface CodeCollision {
  code: string;
  formNames: string[];
}

/** Distinct alternatives, tried in order, before the numeric fallback. */
function candidatesFor(name: string, base: string): string[] {
  const letters = (name.match(/[A-Za-z0-9]+/g) ?? []).map((w) => w.toUpperCase());
  const out: string[] = [];
  if (letters.length > 0) {
    const head = letters[0].slice(0, 3);
    out.push(`${head}${base.slice(-1)}`);
    out.push(letters[0].slice(0, 4));
  }
  if (letters.length > 1) out.push(`${base}${letters[1].slice(0, 1)}`);
  return out.filter((c) => c && c !== base);
}

/**
 * The code for ONE new form, given the codes already taken in the study.
 * What the server uses when it first persists a form that has no code.
 */
export function assignCodeForNewForm(name: string, takenCodes: Iterable<string>): string {
  const taken = new Set<string>();
  for (const c of takenCodes) {
    const t = String(c ?? '').trim().toUpperCase();
    if (t) taken.add(t);
  }
  const base = deriveFormCode(name).toUpperCase();
  if (!taken.has(base)) return base;
  const found = candidatesFor(name, base).find((c) => !taken.has(c.toUpperCase()));
  if (found) return found.toUpperCase();
  let n = 2;
  while (taken.has(`${base}${n}`)) n += 1;
  return `${base}${n}`;
}

/**
 * Assign a unique code to every form, preserving explicit codes.
 * Order is the caller's: the first form carrying a natural code keeps it.
 */
export function assignFormCodes<T extends { name: string; code?: string }>(
  forms: T[],
): Array<T & { code: string }> {
  const taken = new Set<string>();
  for (const f of forms ?? []) {
    const explicit = String(f?.code ?? '').trim();
    if (explicit) taken.add(explicit.toUpperCase());
  }
  const out: Array<T & { code: string }> = [];
  for (const f of forms ?? []) {
    const explicit = String(f?.code ?? '').trim();
    if (explicit) {
      out.push({ ...f, code: explicit });
      continue;
    }
    const code = assignCodeForNewForm(f.name, taken);
    taken.add(code.toUpperCase());
    out.push({ ...f, code });
  }
  return out;
}

/**
 * A name -> code map under the same rule, for readers that key by form name.
 * Replaces the numeric-suffix `buildUniqueFormCodes` on both sides. The first
 * occurrence of a name wins; a stored code is kept and claims its slot.
 */
export function uniqueCodesByName(
  forms: Array<{ name: string; code?: string }>,
): Record<string, string> {
  const firstByName: Array<{ name: string; code?: string }> = [];
  const seen = new Set<string>();
  for (const f of forms ?? []) {
    const name = String(f?.name ?? '').trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    firstByName.push({ name, ...(f.code ? { code: f.code } : {}) });
  }
  const out: Record<string, string> = {};
  for (const f of assignFormCodes(firstByName)) out[f.name] = f.code;
  return out;
}

/** Which codes are shared by more than one form. */
export function findCodeCollisions(
  forms: Array<{ name: string; code?: string }>,
): CodeCollision[] {
  const byCode = new Map<string, string[]>();
  for (const f of forms ?? []) {
    const code = String(f?.code ?? '').trim() || deriveFormCode(f?.name);
    const key = code.toUpperCase();
    byCode.set(key, [...(byCode.get(key) ?? []), f?.name]);
  }
  return [...byCode.entries()]
    .filter(([, names]) => names.length > 1)
    .map(([code, formNames]) => ({ code, formNames }));
}
